import { initializeApp, getApps } from 'firebase/app';
import { getMessaging, getToken, onMessage } from 'firebase/messaging';
// Use the project's configured axios - has baseURL=/api, JWT interceptor, CORS headers
import axios from '../lib/axios';

const firebaseConfig = {
  apiKey:            "AIzaSyCEQ19ULUm9ZPz6Y39_d6FJjF62BJuLeqM",
  authDomain:        "netsights-ai.firebaseapp.com",
  projectId:         "netsights-ai",
  storageBucket:     "netsights-ai.firebasestorage.app",
  messagingSenderId: "927477322022",
  appId:             "1:927477322022:web:f5b66097ebd1dddd0d31e7",
};

const VAPID_KEY = "BH3P9TSmyr-hplK7mkBMbswLreST90fwDEOizxVJ4viCM-lAx0GLL6HCl_E5QI9O2qTLkL-s4I6jtXoU68NBvx0";

// Use relative paths - axios.defaults.baseURL is already set to VITE_API_BASE_URL in App.tsx
// This avoids hardcoded ports and CORS issues in every environment
const PUSH_SAVE_URL   = '/push/token/save';
const PUSH_REMOVE_URL = '/push/token/remove';

// Initialize Firebase only once
const app = getApps().length === 0 ? initializeApp(firebaseConfig) : getApps()[0];
const messaging = getMessaging(app);

// -------------------------------------------------------
// Request permission + get FCM token + save to backend
// -------------------------------------------------------
export const enablePushNotifications = async (shop: string): Promise<boolean> => {
  try {
    if (!('Notification' in window) || !('serviceWorker' in navigator)) {
      console.warn('[Push] Push notifications not supported in this browser');
      return false;
    }

    // Check current permission state first.
    // If already denied, don't attempt requestPermission (it won't show a prompt).
    if (Notification.permission === 'denied') {
      console.warn('[Push] Notification permission previously denied by user');
      return false;
    }

    // Request permission - some browsers (Firefox, Safari) only allow this
    // from a user gesture context. If called programmatically it may return 'default'.
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
      console.warn('[Push] Notification permission not granted:', permission);
      return false;
    }

    // Register service worker with explicit scope for cross-browser compatibility.
    // Firefox/Edge need the SW registered before getToken is called.
    const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js', {
      scope: '/',
    });

    // Wait for the service worker to be active (needed for Firefox/Edge)
    if (registration.installing || registration.waiting) {
      await new Promise<void>((resolve) => {
        const sw = registration.installing || registration.waiting;
        if (!sw) { resolve(); return; }
        sw.addEventListener('statechange', () => {
          if (sw.state === 'activated') resolve();
        });
        // Safety timeout - don't block forever
        setTimeout(resolve, 5000);
      });
    }

    const token = await getToken(messaging, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: registration,
    });

    if (!token) {
      console.error('[Push] No FCM token received');
      return false;
    }

    // Skip backend call if same token is already stored (avoid unnecessary API calls)
    const existingToken = localStorage.getItem('fcm_token');
    if (existingToken === token) {
      console.log('[Push] Token unchanged, skipping backend save');
      return true;
    }

    // Save token to backend - uses app axios (baseURL + auth token already configured)
    await axios.post(PUSH_SAVE_URL, {
      fcm_token: token,
      shop:      shop,
    });

    // Store token locally for logout cleanup
    localStorage.setItem('fcm_token', token);

    console.log('[Push] Push notifications enabled for shop:', shop);
    return true;

  } catch (error) {
    console.error('[Push] enablePushNotifications error:', error);
    return false;
  }
};

// -------------------------------------------------------
// Remove token on logout
// -------------------------------------------------------
export const disablePushNotifications = async (): Promise<void> => {
  try {
    const token = localStorage.getItem('fcm_token');
    if (!token) return;

    await axios.post(PUSH_REMOVE_URL, {
      fcm_token: token,
    });

    localStorage.removeItem('fcm_token');
    console.log('Push notifications disabled');

  } catch (error) {
    console.error('disablePushNotifications error:', error);
  }
};

// -------------------------------------------------------
// Foreground message listener
// Call this once in App.tsx
// -------------------------------------------------------
export const onForegroundMessage = (
  callback: (title: string, body: string) => void
): (() => void) => {
  return onMessage(messaging, (payload) => {
    const title = payload.notification?.title ?? 'Netsight';
    const body  = payload.notification?.body  ?? '';
    console.log(body);
    callback(title, body);
  });
};